import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

/**
 * Handles a product select event by replacing the current media gallery with a new one.
 *
 * @param {ProductSelectEvent} event - The product select event.
 */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Compare unique media ids as a set (order-insensitive).
        // Size changes often only reorder featured media — remounting causes jitter.
        const mediaIdSet = (root) =>
          [
            ...new Set(
              [...root.querySelectorAll('[data-media-id]')]
                .map((el) => el.getAttribute('data-media-id') || '')
                .filter(Boolean)
            ),
          ]
            .sort()
            .join('|');

        const currentIds = mediaIdSet(this);
        const nextIds = mediaIdSet(newMediaGallery);

        if (currentIds && nextIds && currentIds === nextIds) {
          return;
        }

        // No media ids found — fall back to container count only
        if (!currentIds && !nextIds) {
          const count = (root) => root.querySelectorAll('.product-media-container').length;
          if (count(this) === count(newMediaGallery)) return;
        }

        // Same set already handled above; if one side is empty, keep the live gallery
        // rather than flashing an empty remount.
        if (currentIds && !nextIds) {
          return;
        }

        // Preserve slide index across remount (pixel-based pair slider)
        const existingGallery = this.querySelector('.anaya-pdp-gallery');
        const existingTrack = this.querySelector('.anaya-pdp-gallery__track');
        if (existingTrack && existingGallery) {
          const transform = existingTrack.style.transform || '';
          const match = transform.match(/translate3d\(\s*-?([\d.]+)px/);
          if (match) {
            const perPage = window.matchMedia('(min-width: 750px)').matches ? 2 : 1;
            const gap = 12;
            const viewportWidth = existingGallery.clientWidth || 0;
            const slideWidth =
              perPage > 0 ? (viewportWidth - gap * (perPage - 1)) / perPage : viewportWidth;
            const step = slideWidth + gap;
            const idx = step > 0 ? Math.round(Number(match[1]) / step) : 0;
            document.documentElement.dataset.anayaPdpGalleryIndex = String(Math.max(0, idx));
          }
        }

        this.replaceWith(newMediaGallery);
        document.dispatchEvent(
          new CustomEvent('anaya:media-gallery-replaced', { bubbles: true, detail: { gallery: newMediaGallery } })
        );
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
